/** Runes: the Frostmark secondary skill, as this game defines it.
 *
 * A stack of an army whose hero knows Runes accumulates **rune levels** as it
 * fights, and each level is worth a fixed stat step. The levels are earned, not
 * bought - there is nothing to spend and no choice to make - so the whole skill
 * is a counter and a table:
 *
 *     +1 rune level when the stack attacks   (once per turn, however many
 *                                             attacks the creature makes)
 *     +2 when it takes damage
 *     +3 when it takes a defensive stance
 *
 *     level 1, 4, 7 -> +2 attack
 *     level 2, 5, 8 -> +2 defense
 *     level 3, 6, 9 -> +1 speed        cumulative, capped by skill level
 *
 * Basic caps at 3 levels, Advanced at 6, Expert at 9 - so an Expert hero's
 * stack tops out at +6/+6/+3.
 *
 * **No new `StackStore` column.** The level is not stored; it is *counted* from
 * the modifiers already on the stack, one per level under `SRC_RUNE_PREFIX`.
 * That keeps the save format and every battle hash untouched, it survives
 * `undoTo` for free because the journal already covers `mods`, and it makes the
 * stat bonus fall out of the modifier system rather than needing a second rule
 * to apply it. The counter and the effect are the same object.
 *
 * The Snowhulk Runecarver is the one creature that runs this on its own: it always
 * behaves as though its hero had Expert Runes, and a hero who *also* has the
 * skill stacks additively with it, to +12/+12/+6 at nine levels. That is folded
 * into the value of each level's modifier rather than into a second modifier,
 * so counting the modifiers still gives the level.
 */

import type { ContentRegistry } from "../content/registry.js";
import type { CreatureDefId } from "../ids.js";
import type { Modifier, StatKey } from "../modifiers.js";
import { asModifierSrcId } from "../ids.js";
import type { BattleState } from "./state.js";
import { F_ALIVE } from "./state.js";
import { abilitiesOf } from "./abilities.js";
import { addMod, type Ctx } from "./apply.js";

import { STAT_RUNES, STAT_RUNE_CAP } from "./stats.js";
import { resolveStat } from "../modifiers.js";

/** One modifier per earned level, and the level is how many there are. */
const SRC_RUNE_PREFIX = "battle.rune.";
/**
 * The once-per-turn latch for attacking, deliberately NOT under the prefix
 * above: `battle.runeturn` does not start with `battle.rune.`, so it is never
 * miscounted as a level. Cleared with the rest of a stack's turn state.
 */
export const SRC_RUNE_TURN = asModifierSrcId("battle.runeturn");

/** How many levels each skill level allows. Index is 0..3. */
export const RUNE_CAP: readonly number[] = Object.freeze([0, 3, 6, 9]);
/** A Snowhulk Runecarver is always its own Expert. */
export const RUNEMASTER_CAP = 9;

/** What one level is worth, by its position in the repeating triple. */
function stepFor(level: number): { readonly stat: StatKey; readonly value: number } {
  const phase = level % 3;
  if (phase === 1) return { stat: "attack", value: 2 };
  if (phase === 2) return { stat: "defense", value: 2 };
  return { stat: "speed", value: 1 };
}

/** The rune levels this stack has already earned. */
export function runeLevelOf(s: BattleState, slot: number): number {
  let n = 0;
  for (const m of s.stacks.mods[slot] ?? []) {
    if ((m.source as string).startsWith(SRC_RUNE_PREFIX)) n++;
  }
  return n;
}

/** Does this creature carry its own Expert Runes, whatever its hero knows? */
function isRunemaster(content: ContentRegistry, def: CreatureDefId): boolean {
  for (const a of abilitiesOf(content, def)) if (a.kind === "RUNE_MASTER") return true;
  return false;
}

/**
 * The hero's Runes level as it reached this stack.
 *
 * Installed army-wide by `startBattle` exactly like the war-machine crew
 * skills, so a stack can be asked without the battle knowing which hero it
 * belongs to.
 */
function heroRuneLevel(s: BattleState, slot: number): number {
  const mine = (s.stacks.mods[slot] ?? []).filter((m) => m.stat === STAT_RUNES);
  return resolveStat(0, mine).value;
}

/** Extra ceiling the hero's kit grants over `RUNE_CAP`, never the levels' cost. */
function heroRuneCapBonus(s: BattleState, slot: number): number {
  const mine = (s.stacks.mods[slot] ?? []).filter((m) => m.stat === STAT_RUNE_CAP);
  return resolveStat(0, mine).value;
}

/**
 * Grant `n` rune levels to a stack, respecting its cap.
 *
 * Silent and free when nothing about the stack knows the skill, which is the
 * common case: every battle in the game calls this on every attack.
 */
export function gainRunes(c: Ctx, content: ContentRegistry, slot: number, n: number): void {
  const s = c.s;
  if (slot < 0 || slot >= s.stacks.n) return;
  if ((s.stacks.flags[slot]! & F_ALIVE) === 0) return;

  const taught = RUNE_CAP[Math.max(0, Math.min(3, heroRuneLevel(s, slot)))] ?? 0;
  // A wider ceiling is only a ceiling: the hero still pays for levels up to
  // what he was taught, so the extra levels a Deep Rune allows are worth their
  // step only to a stack that also has a Runemaster's own claim on them.
  const heroCap = taught === 0 ? 0 : taught + heroRuneCapBonus(s, slot);
  const master = isRunemaster(content, s.stacks.def[slot]!);
  const cap = Math.max(heroCap, master ? RUNEMASTER_CAP : 0);
  if (cap === 0) return;

  let level = runeLevelOf(s, slot);
  for (let i = 0; i < n && level < cap; i++) {
    level++;
    // Two sources may pay for the same level, and they add: the creature's own
    // Expert and the hero's skill. Folded into one modifier's value so that
    // counting modifiers still counts levels.
    const stack = (master ? 1 : 0) + (level <= heroCap ? 1 : 0);
    if (stack === 0) continue;
    const step = stepFor(level);
    const mod: Modifier = {
      source: asModifierSrcId(`${SRC_RUNE_PREFIX}${level}`),
      stat: step.stat,
      op: "add",
      value: step.value * stack,
      expires: { kind: "battle" },
    };
    addMod(c, slot, mod);
  }
}
